/**
 * Choosing which recording you sit with today.
 *
 * Ajahn Brahm is a storyteller. Across twenty years he returns to the same handful of
 * anecdotes — the two bad bricks, the chicken and the eagle, the man who lost his
 * buffalo — and hearing one twice in a week does more damage than any audio artefact
 * could. So the selector's real job is not "find something good", it is "find something
 * good that does not repeat what you have just heard".
 *
 * Scoring is a sum of penalties against a base of zero. Nothing is a hard filter except
 * reachability and the recency exclusion, because a hard filter on a corpus of a hundred
 * gets you stuck; a penalty degrades gracefully when the pool runs thin.
 */

import type { Cadence, Manifest, Recording } from '../types';
import { isEligible } from './eligibility.ts';
import { queryCompositionCatalog } from './composition-catalog.ts';

// ---------------------------------------------------------------- history

export interface PlayRecord {
  recordingId: string;
  /** Epoch ms. */
  at: number;
  /** Stories actually reached — a sit ended early may not have got to all of them. */
  storiesHeard: string[];
  themes: string[];
  /** Whether the user finished. Abandoned sits are weak evidence of a bad match. */
  completed: boolean;
}

export interface SelectionContext {
  history: PlayRecord[];
  now: number;
  targetSec: number;
  cadence: Cadence;
  /** Zero for a clock deadline; presets retain the planner's 30-second tolerance. */
  toleranceSec?: number;
  /** Optional spoken share, using reviewed phase alternatives only. */
  desiredSpeechRatio?: number;
  /** User-chosen theme filter. Empty means no filter. */
  themes?: string[];
  /** Deterministic source of randomness, for tests and for reproducible sessions. */
  random?: () => number;
  /**
   * Recording id → recorded-on epoch ms, for the era penalty. Filled in by
   * `selectRecording` from the manifest so history entries stay small and stable.
   */
  recordingDates?: Map<string, number>;
  excludeIds?: string[];
}

// ---------------------------------------------------------------- tunables

const DAY = 86_400_000;

/** A recording heard inside this window is not offered again at all. */
const RECORDING_EXCLUSION_DAYS = 60;

/** Half-lives for the decaying penalties. A story fades slower than a theme. */
const STORY_HALF_LIFE_DAYS = 90;
const THEME_HALF_LIFE_DAYS = 14;

const STORY_PENALTY = 60;      // dominant: never tell the same story twice in a season
const THEME_PENALTY = 8;       // mild: themes recur naturally and that is fine
const ERA_PENALTY = 6;         // two sits from the same month of 2013 feel samey
const ERA_WINDOW_DAYS = 120;   // how close two recording dates must be to count as an era
const ABANDONED_PENALTY = 15;  // they stopped part-way last time; try something else
const UNPLAYED_BONUS = -10;    // gently favour what has never been heard

/** How many of the best candidates go into the weighted draw. */
const SHORTLIST = 5;

/**
 * How many recent sits are pushed down a shuffle.
 *
 * The 60-day exclusion below is the real defence, but it is relaxed when it would empty
 * the pool, and the library's shuffle has no exclusion at all. Twelve is about a
 * fortnight of daily practice: long enough that a shuffle does not hand back last
 * Tuesday, short enough that it does not quietly become an ordering.
 */
const RECENT_SINK = 12;

// ---------------------------------------------------------------- shuffling

/**
 * A seeded stream.
 *
 * The library shuffles on every render, and an unseeded shuffle would reorder the list
 * under the reader's thumb every time a search character is typed. The seed is the
 * thing that changes when someone asks for a new shuffle; nothing else moves it.
 */
export function seededRandom(seed: number): () => number {
  let s = (Math.floor(seed) * 2654435761) >>> 0 || 1;
  const next = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  // An LCG's first output barely moves between neighbouring seeds; discard the run-in.
  for (let i = 0; i < 8; i++) next();
  return next;
}

/**
 * Fisher-Yates.
 *
 * Exported because the library shuffles by the same rule, and because "shuffled" is a
 * claim worth being able to test rather than eyeball.
 */
export function shuffled<T>(items: readonly T[], random: () => number = Math.random): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Shuffle, then sink whatever was heard in the last few sits.
 *
 * This is what "shuffle" has to mean here. A plain shuffle over 248 recordings will
 * cheerfully offer the one from Tuesday, and the whole reason this app keeps a history
 * is that hearing the same anecdote twice in a week is the one failure a listener
 * actually notices.
 *
 * The sort is stable, so everything not recently heard keeps its shuffled order and only
 * the recent entries move — to the bottom, most recent last.
 */
export function shuffleAvoidingRecent<T>(
  items: readonly T[],
  idOf: (item: T) => string,
  history: readonly { recordingId: string; at: number }[],
  random: () => number = Math.random,
  recentCount = RECENT_SINK,
): T[] {
  const rank = new Map<string, number>();
  const tail = history.slice().sort((a, b) => a.at - b.at).slice(-recentCount);
  tail.forEach((h, i) => rank.set(h.recordingId, i + 1));
  return shuffled(items, random)
    .sort((a, b) => (rank.get(idOf(a)) ?? 0) - (rank.get(idOf(b)) ?? 0));
}

// ---------------------------------------------------------------- scoring

function decay(ageMs: number, halfLifeDays: number): number {
  return Math.pow(0.5, ageMs / (halfLifeDays * DAY));
}

export interface ScoredCandidate {
  recording: Recording;
  score: number;
  /** Why it scored what it did — surfaced in the debug UI, and useful in tests. */
  reasons: string[];
}

export function scoreRecording(rec: Recording, ctx: SelectionContext): ScoredCandidate {
  const reasons: string[] = [];
  let score = 0;

  const played = ctx.history.filter((h) => h.recordingId === rec.id);
  if (played.length === 0) {
    score += UNPLAYED_BONUS;
    reasons.push('never heard');
  }

  // Stories: the heavy one. A story heard yesterday all but rules its recording out;
  // one heard three months ago barely registers.
  const storyWeight = new Map<string, number>();
  for (const h of ctx.history) {
    const w = decay(ctx.now - h.at, STORY_HALF_LIFE_DAYS);
    for (const s of h.storiesHeard) {
      storyWeight.set(s, Math.max(storyWeight.get(s) ?? 0, w));
    }
  }
  for (const s of rec.stories) {
    const w = storyWeight.get(s.id);
    if (w && w > 0.02) {
      score += STORY_PENALTY * w;
      reasons.push(`story "${s.id}" heard recently (${(w * 100).toFixed(0)}%)`);
    }
  }

  const themeWeight = new Map<string, number>();
  for (const h of ctx.history) {
    const w = decay(ctx.now - h.at, THEME_HALF_LIFE_DAYS);
    for (const t of h.themes) {
      themeWeight.set(t, Math.max(themeWeight.get(t) ?? 0, w));
    }
  }
  for (const t of rec.themes) {
    const w = themeWeight.get(t);
    if (w && w > 0.05) score += THEME_PENALTY * w;
  }

  // Era: recordings made within a few months of each other share a preoccupation and
  // often a turn of phrase. Spreading across the twenty years is part of the appeal.
  const recDate = Date.parse(rec.recordedOn);
  if (!Number.isNaN(recDate)) {
    for (const h of ctx.history.slice(-6)) {
      const prevDate = ctx.recordingDates?.get(h.recordingId);
      if (prevDate === undefined) continue;
      if (Math.abs(recDate - prevDate) < ERA_WINDOW_DAYS * DAY) {
        score += ERA_PENALTY * decay(ctx.now - h.at, THEME_HALF_LIFE_DAYS);
        reasons.push('recorded around the same time as a recent sit');
        break;
      }
    }
  }

  if (played.some((h) => !h.completed)) {
    score += ABANDONED_PENALTY;
    reasons.push('left unfinished before');
  }

  return { recording: rec, score, reasons };
}

// ---------------------------------------------------------------- selection

export interface Selection {
  chosen: Recording;
  shortlist: ScoredCandidate[];
  /** Candidates that were eligible before the shortlist cut. */
  poolSize: number;
}

export class NoCandidatesError extends Error {
  readonly reason: 'duration' | 'themes' | 'recency';

  constructor(reason: 'duration' | 'themes' | 'recency') {
    super(`no recording available (${reason})`);
    this.name = 'NoCandidatesError';
    this.reason = reason;
  }
}

export function selectRecording(manifest: Manifest, ctx: SelectionContext): Selection {
  const random = ctx.random ?? Math.random;
  const dates = new Map<string, number>();
  for (const r of manifest.recordings) {
    const t = Date.parse(r.recordedOn);
    if (!Number.isNaN(t)) dates.set(r.id, t);
  }
  const fullCtx: SelectionContext = { ...ctx, recordingDates: dates };

  const { pool } = selectionPool(manifest, ctx);

  // 4. Shuffle before ranking.
  //
  //    Not decoration. Every recording a fresh install has never heard scores exactly
  //    UNPLAYED_BONUS and nothing else, so the whole pool ties — and `sort` is stable, so
  //    the shortlist was simply the first five in manifest order. Measured over 2,000
  //    fresh-install picks: 5 of 248 recordings could ever be chosen and one of them took
  //    44.3%. Shuffling first and sorting stably makes the tiebreak random while leaving
  //    every real score difference exactly where it was.
  const scored = shuffleAvoidingRecent(pool, (r) => r.id, ctx.history, random)
    .map((r) => scoreRecording(r, fullCtx))
    .sort((a, b) => a.score - b.score);

  const shortlist = scored.slice(0, SHORTLIST);

  // 5. Weighted draw over the shortlist, so the same morning routine does not produce
  //    the same recording every day, while still respecting the ranking.
  const weights = shortlist.map((c, i) => Math.pow(0.6, i) * (c.score < 0 ? 1.3 : 1));
  const total = weights.reduce((a, b) => a + b, 0);
  let roll = random() * total;
  let chosen = shortlist[0];
  for (let i = 0; i < shortlist.length; i++) {
    roll -= weights[i];
    if (roll <= 0) { chosen = shortlist[i]; break; }
  }

  return { chosen: chosen.recording, shortlist, poolSize: scored.length };
}

/** What the user is told about why they got this one. Kept short and human. */
export function explainChoice(sel: Selection, ctx: SelectionContext): string {
  const rec = sel.chosen;
  const heard = ctx.history.some((h) => h.recordingId === rec.id);
  const date = new Date(rec.recordedOn).toLocaleDateString('en-GB',
    { day: 'numeric', month: 'long', year: 'numeric' });
  if (!heard) return `Recorded ${date}. You haven't heard this one.`;
  const last = Math.max(...ctx.history.filter((h) => h.recordingId === rec.id).map((h) => h.at));
  const days = Math.round((ctx.now - last) / DAY);
  return `Recorded ${date}. You last heard this ${days} days ago.`;
}

/** Read-only pool accounting uses the same filters without consuming randomness. */
export function selectionPool(manifest: Manifest, ctx: SelectionContext) {
  // 0. Withheld recordings, first and unconditionally.
  //
  //    A recording is withheld because a sit that ends on it ends wrongly — on the
  //    opening of a Dhamma talk, a question-and-answer session, or society business
  //    rather than on the end of a meditation. Unlike the recency rule below, this is
  //    never relaxed when the pool runs thin: a small library is not a reason to hand
  //    someone a news item at the end of twenty minutes of silence.
  //
  //    The list is generated from data/decisions/endings-v1.json, where every entry
  //    carries the two independent proposals that agreed on it and the evidence each
  //    quoted. See docs/COMPOSITION-AUDIT.md.
  let pool = manifest.recordings.filter(r => !ctx.excludeIds?.includes(r.id));

  // 1. Release policy and reachability. Not a preference — a recording that cannot make 45 minutes simply
  //    is not a candidate for a 45-minute sit.
  const indexed = queryCompositionCatalog(manifest,ctx.targetSec,ctx.cadence,ctx.toleranceSec,ctx.desiredSpeechRatio);
  pool = pool.filter((recording) => indexed ? indexed.has(recording.id) : isEligible({
    recording, targetSec: ctx.targetSec, cadence: ctx.cadence,
    toleranceSec: ctx.toleranceSec, desiredSpeechRatio: ctx.desiredSpeechRatio,
  }));
  if (pool.length === 0) throw new NoCandidatesError('duration');

  // 2. Theme filter, if the user set one.
  if (ctx.themes && ctx.themes.length) {
    const want = new Set(ctx.themes);
    const filtered = pool.filter((r) => r.themes.some((t) => want.has(t)));
    if (filtered.length === 0) throw new NoCandidatesError('themes');
    pool = filtered;
  }

  // 3. Recency exclusion. Relaxed rather than enforced if it would empty the pool —
  //    a user with a small offline library should still get a sit.
  const cutoff = ctx.now - RECORDING_EXCLUSION_DAYS * DAY;
  const recent = new Set(ctx.history.filter((h) => h.at > cutoff).map((h) => h.recordingId));
  const fresh = pool.filter((r) => !recent.has(r.id));
  const poolSize = pool.length;
  if (fresh.length > 0) pool = fresh;
  return { pool, poolSize, freshPoolSize: fresh.length, recencyRelaxed: fresh.length === 0 };

}
